import React, { useEffect, useLayoutEffect, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  direction?: 'rtl' | 'ltr';
}

export interface RichTextEditorRef {
  getValue: () => string;
  syncValue: () => void; // Force sync current value to onChange
}

// Helper: Convert Quill HTML to standard HTML tags
const convertQuillToStandardHtml = (html: string): string => {
  if (!html || html.trim() === '' || html === '<p><br></p>' || html === '<p></p>') {
    return '';
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;

    if (!body || body.children.length === 0) {
      return html; // Return original if parsing fails
    }

    // Helper: Wrap element children with a tag
    const wrapChildren = (element: Element, tagName: string) => {
      if (element.querySelector(tagName)) return; // Already has the tag
      const wrapper = doc.createElement(tagName);
      while (element.firstChild) {
        wrapper.appendChild(element.firstChild);
      }
      element.appendChild(wrapper);
    };

    // Convert ql-underline class to <u> tag
    body.querySelectorAll('[class*="ql-underline"]').forEach((el) => {
      el.classList.remove('ql-underline');
      wrapChildren(el, 'u');
      if (el.classList.length === 0) el.removeAttribute('class');
    });

    // Convert ql-italic class to <em> tag
    body.querySelectorAll('[class*="ql-italic"]').forEach((el) => {
      el.classList.remove('ql-italic');
      wrapChildren(el, 'em');
      if (el.classList.length === 0) el.removeAttribute('class');
    });

    // Convert ql-bold class to <strong> tag
    body.querySelectorAll('[class*="ql-bold"]').forEach((el) => {
      el.classList.remove('ql-bold');
      wrapChildren(el, 'strong');
      if (el.classList.length === 0) el.removeAttribute('class');
    });

    // Convert inline styles to semantic tags
    body.querySelectorAll('[style]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const style = el.getAttribute('style') || '';
      
      const hasUnderline = /text-decoration[^:]*:\s*underline/i.test(style);
      const hasItalic = /font-style[^:]*:\s*italic/i.test(style);
      const hasBold = /font-weight[^:]*:\s*(bold|[6-9]\d{2})/i.test(style);

      if (hasUnderline) wrapChildren(el, 'u');
      if (hasItalic) wrapChildren(el, 'em');
      if (hasBold) wrapChildren(el, 'strong');

      // Clean up the style attribute if we converted it
      if (hasUnderline || hasItalic || hasBold) {
        const cleanedStyle = style
          .split(';')
          .map((decl) => decl.trim())
          .filter((decl) => {
            const lower = decl.toLowerCase();
            return !(
              (hasUnderline && lower.includes('text-decoration')) ||
              (hasItalic && lower.includes('font-style')) ||
              (hasBold && lower.includes('font-weight'))
            );
          })
          .filter(Boolean)
          .join('; ');
        
        if (cleanedStyle) {
          el.setAttribute('style', cleanedStyle);
        } else {
          el.removeAttribute('style');
        }
      }
    });

    return body.innerHTML;
  } catch (error) {
    console.error('Error converting Quill HTML:', error);
    return html; // Return original on error
  }
};

// Helper: Normalize pasted HTML from Word/Google Docs
const normalizePastedHtml = (html: string): string => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;

    // Remove unwanted elements
    body.querySelectorAll('meta, style, link, script, o\\:p').forEach((el) => el.remove());

    // Convert <b> to <strong>, <i> to <em>
    body.querySelectorAll<HTMLElement>('b').forEach((el) => {
      const strong = doc.createElement('strong');
      strong.innerHTML = el.innerHTML;
      el.replaceWith(strong);
    });

    body.querySelectorAll<HTMLElement>('i').forEach((el) => {
      const em = doc.createElement('em');
      em.innerHTML = el.innerHTML;
      el.replaceWith(em);
    });

    // Convert Word headings (MsoHeading) to h1-h6
    body.querySelectorAll<HTMLElement>('p, div').forEach((el) => {
      const className = el.className || '';
      const headingMatch = className.match(/MsoHeading\s*([1-6])/i);
      if (headingMatch) {
        const level = parseInt(headingMatch[1], 10);
        const heading = doc.createElement(`h${level}`);
        heading.innerHTML = el.innerHTML;
        el.replaceWith(heading);
      }
    });

    // Clean up Mso classes
    body.querySelectorAll<HTMLElement>('[class]').forEach((el) => {
      const cleanedClasses = el.className
        .split(/\s+/)
        .filter((cls) => cls && !/^Mso/i.test(cls))
        .join(' ');
      if (cleanedClasses) {
        el.className = cleanedClasses;
      } else {
        el.removeAttribute('class');
      }
    });

    return body.innerHTML;
  } catch (error) {
    console.error('Error normalizing pasted HTML:', error);
    return html;
  }
};

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value = '',
  onChange,
  placeholder = 'اكتب المحتوى هنا...',
  height = '300px',
  direction = 'rtl',
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const isInternalUpdateRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializingRef = useRef(false);
  const handleTextChangeRef = useRef<(() => void) | null>(null);
  const handlePasteRef = useRef<((e: ClipboardEvent) => void) | null>(null);
  const instanceIdRef = useRef<string>(`quill-${Math.random().toString(36).substr(2, 9)}`);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: ['ArabType', 'Segoe UI', 'Tahoma', 'Arial', 'sans-serif'] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ align: ['', 'right', 'center', 'justify'] }],
          [{ color: [] }, { background: [] }],
          ['link', 'image', 'video'],
          ['clean'],
        ],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  // Initialize Quill - use useEffect to avoid React StrictMode double-call issues
  useEffect(() => {
    const container = editorRef.current;
    if (!container) return;
    
    const instanceId = instanceIdRef.current;
    
    // Check if ref is already set and Quill is working
    if (quillRef.current) {
      console.log('RichTextEditor: quillRef already set, skipping', instanceId);
      return;
    }
    
    // Check if initialization is in progress
    if (isInitializingRef.current) {
      console.log('RichTextEditor: Initialization in progress, skipping', instanceId);
      return;
    }
    
    // Check instance ID - if same instance, skip
    if (container.dataset.quillInstanceId === instanceId) {
      console.log('RichTextEditor: Instance ID matches, skipping', instanceId);
      return;
    }
    
    // Check for existing Quill elements - if found, clean them up first
    const existingToolbar = container.querySelector('.ql-toolbar');
    const existingContainer = container.querySelector('.ql-container');
    
    if (existingToolbar || existingContainer) {
      // Clean up any existing Quill elements before reinitializing
      if (existingToolbar) existingToolbar.remove();
      if (existingContainer) {
        existingContainer.innerHTML = '';
        existingContainer.remove();
      }
      container.innerHTML = '';
    }
    
    // Mark as initializing IMMEDIATELY (synchronously, before any async)
    isInitializingRef.current = true;
    container.dataset.quillInstanceId = instanceId;
    container.dataset.quillInitialized = 'true';

    console.log('RichTextEditor: Initializing Quill', instanceId);

    let quill: Quill;
    try {
      quill = new Quill(container, {
      theme: 'snow',
      placeholder,
      modules,
      formats: [
        'header',
        'font',
        'size',
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'list',
        'bullet',
        'indent',
        'script',
        'align',
        'color',
        'background',
        'link',
        'image',
        'video',
      ],
    });

    // Set direction and font
    quill.root.setAttribute('dir', direction);
    quill.root.style.textAlign = direction === 'rtl' ? 'right' : 'left';
    quill.root.style.fontFamily =
      direction === 'rtl'
        ? "'ArabType', 'Segoe UI', 'Tahoma', 'Arial', sans-serif"
        : "'Segoe UI', 'Tahoma', 'Arial', sans-serif";

    // Handle paste from Word/Google Docs
    const handlePaste = (e: ClipboardEvent) => {
      const html = e.clipboardData?.getData('text/html');
      if (!html) return;

      // Check if it's from Word or Google Docs
      const isWord = /class=["']?Mso|mso-|w:WordDocument/i.test(html);
      const isGoogleDocs = /id=["']docs-internal-guid/i.test(html);

      if (isWord || isGoogleDocs) {
        e.preventDefault();
        const normalized = normalizePastedHtml(html);
        const selection = quill.getSelection(true);
        const index = selection ? selection.index : quill.getLength();
        quill.clipboard.dangerouslyPasteHTML(index, normalized, 'user');
      }
    };

    // Save handler to ref for cleanup
    handlePasteRef.current = handlePaste;
    quill.root.addEventListener('paste', handlePaste);

    // Handle text changes - convert Quill HTML to standard HTML
    const handleTextChange = () => {
      console.log('RichTextEditor: handleTextChange called', {
        isInternalUpdate: isInternalUpdateRef.current,
      });
      
      if (isInternalUpdateRef.current) {
        console.log('RichTextEditor: Skipping - isInternalUpdate is true');
        return;
      }

      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Debounce the onChange call
      timeoutRef.current = setTimeout(() => {
        if (!quillRef.current) {
          console.warn('RichTextEditor: quillRef.current is null in handleTextChange');
          return;
        }

        const quillHtml = quillRef.current.root.innerHTML;
        const standardHtml = convertQuillToStandardHtml(quillHtml);
        
        console.log('RichTextEditor: Calling onChange', {
          quillHtmlLength: quillHtml.length,
          standardHtmlLength: standardHtml.length,
          isEmpty: !standardHtml || standardHtml.length === 0,
          standardHtml: standardHtml.substring(0, 100), // First 100 chars for debugging
        });
        
        // Always call onChange to ensure parent state is updated
        onChange(standardHtml);
        console.log('RichTextEditor: onChange called with value:', standardHtml.substring(0, 100));
      }, 100); // 100ms debounce
    };

    // Save handler to ref for cleanup
    handleTextChangeRef.current = handleTextChange;
    quill.on('text-change', handleTextChange);
    
    console.log('RichTextEditor: Handler registered', {
      hasQuill: !!quill,
      hasHandler: !!handleTextChange,
    });

    // Set initial content
    if (value) {
      console.log('RichTextEditor: Setting initial content', {
        valueLength: value.length,
      });
      isInternalUpdateRef.current = true;
      quill.clipboard.dangerouslyPasteHTML(value, 'silent');
      // Use setTimeout to ensure isInternalUpdate is reset after Quill processes the content
      setTimeout(() => {
        isInternalUpdateRef.current = false;
        console.log('RichTextEditor: isInternalUpdate reset to false');
      }, 0);
    } else {
      isInternalUpdateRef.current = false;
    }

      quillRef.current = quill;
      isInitializingRef.current = false; // Mark initialization as complete
    } catch (error) {
      // If initialization fails, reset flags and clean up
      console.error('Error initializing Quill:', error);
      isInitializingRef.current = false;
      if (container) {
        container.removeAttribute('data-quill-initialized');
        // Clean up any partial initialization
        const toolbar = container.querySelector('.ql-toolbar');
        const qlContainer = container.querySelector('.ql-container');
        if (toolbar) toolbar.remove();
        if (qlContainer) {
          qlContainer.innerHTML = '';
          qlContainer.remove();
        }
        container.innerHTML = '';
      }
      return; // Exit early if initialization failed
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (quillRef.current) {
        // Use refs for cleanup handlers
        if (handleTextChangeRef.current) {
          quillRef.current.off('text-change', handleTextChangeRef.current);
        }
        if (handlePasteRef.current) {
          quillRef.current.root.removeEventListener('paste', handlePasteRef.current);
        }
        
        // Clean up Quill instance
        try {
          const container = editorRef.current;
          if (container) {
            const toolbar = container.querySelector('.ql-toolbar');
            const qlContainer = container.querySelector('.ql-container');
            if (toolbar) toolbar.remove();
            if (qlContainer) {
              qlContainer.innerHTML = '';
              qlContainer.remove();
            }
            container.innerHTML = '';
            container.removeAttribute('data-quill-initialized');
            container.removeAttribute('data-quill-instance-id');
          }
        } catch (e) {
          console.warn('Error cleaning up Quill:', e);
        }
      }
      quillRef.current = null;
      isInitializingRef.current = false; // Reset initialization flag
      handleTextChangeRef.current = null;
      handlePasteRef.current = null;
    };
  }, []); // Only run once on mount

  // Update Quill content when value prop changes (from parent)
  useEffect(() => {
    console.log('RichTextEditor: value prop changed', {
      valueLength: value ? value.length : 0,
      valuePreview: value ? value.substring(0, 100) : 'EMPTY',
      hasQuill: !!quillRef.current,
      isInternalUpdate: isInternalUpdateRef.current,
    });
    
    if (!quillRef.current || isInternalUpdateRef.current) {
      console.log('RichTextEditor: Skipping update - no quill or internal update');
      return;
    }

    const currentHtml = quillRef.current.root.innerHTML;
    const normalizedCurrent = currentHtml === '<p><br></p>' || currentHtml === '<p></p>' ? '' : currentHtml;
    const normalizedValue = value || '';

    console.log('RichTextEditor: Comparing values', {
      currentLength: normalizedCurrent.length,
      newValueLength: normalizedValue.length,
      areDifferent: normalizedCurrent !== normalizedValue,
    });

    // Only update if different
    if (normalizedCurrent !== normalizedValue) {
      console.log('RichTextEditor: Updating Quill content');
      isInternalUpdateRef.current = true;
      const selection = quillRef.current.getSelection();
      
      if (normalizedValue) {
        quillRef.current.clipboard.dangerouslyPasteHTML(normalizedValue, 'silent');
      } else {
        quillRef.current.setText('');
      }

      // Restore selection
      if (selection) {
        setTimeout(() => {
          if (quillRef.current) {
            quillRef.current.setSelection(selection);
          }
        }, 0);
      }

      setTimeout(() => {
        isInternalUpdateRef.current = false;
        console.log('RichTextEditor: isInternalUpdate reset to false after value update');
      }, 0);
    } else {
      console.log('RichTextEditor: Values are the same, skipping update');
    }
  }, [value]);

  // Expose getValue method via ref
  useImperativeHandle(ref, () => ({
    getValue: () => {
      // Try to get value from Quill instance first
      if (quillRef.current) {
        const quillHtml = quillRef.current.root.innerHTML;
        const standardHtml = convertQuillToStandardHtml(quillHtml);
        console.log('RichTextEditor: getValue called from Quill', {
          quillHtmlLength: quillHtml.length,
          standardHtmlLength: standardHtml.length,
        });
        return standardHtml;
      }
      
      // Fallback: Try to get value from DOM directly
      if (editorRef.current) {
        const qlEditor = editorRef.current.querySelector('.ql-editor');
        if (qlEditor) {
          const domHtml = qlEditor.innerHTML;
          const standardHtml = convertQuillToStandardHtml(domHtml);
          console.log('RichTextEditor: getValue called from DOM', {
            domHtmlLength: domHtml.length,
            standardHtmlLength: standardHtml.length,
          });
          return standardHtml;
        }
      }
      
      // Last fallback: Return value prop
      console.warn('RichTextEditor: quillRef.current is null and DOM not available, returning value prop');
      return value || '';
    },
    syncValue: () => {
      // Force sync current value to onChange (bypass debounce)
      if (quillRef.current) {
        const quillHtml = quillRef.current.root.innerHTML;
        const standardHtml = convertQuillToStandardHtml(quillHtml);
        console.log('RichTextEditor: syncValue called', {
          quillHtmlLength: quillHtml.length,
          standardHtmlLength: standardHtml.length,
        });
        onChange(standardHtml);
      } else if (editorRef.current) {
        const qlEditor = editorRef.current.querySelector('.ql-editor');
        if (qlEditor) {
          const domHtml = qlEditor.innerHTML;
          const standardHtml = convertQuillToStandardHtml(domHtml);
          console.log('RichTextEditor: syncValue called from DOM', {
            domHtmlLength: domHtml.length,
            standardHtmlLength: standardHtml.length,
          });
          onChange(standardHtml);
        }
      }
    },
  }), [value, onChange]);

  return (
    <div className="rich-text-editor-wrapper" style={{ direction }}>
      <div
        ref={editorRef}
        style={{
          height,
          maxHeight: height,
          marginBottom: '42px',
          overflow: 'hidden',
        }}
      />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
